import { api } from '../console/api.ts';
import { type CarrierView, carrierName } from '../console/carriers.ts';
import { empty, field, pageHead, pill } from '../console/components.ts';
import { InputError } from '../console/errors.ts';
import { cap, date, esc, icon, PRODUCT, when } from '../console/format.ts';
import { type Actions, current, render } from '../console/router.ts';
import { canAskKnowledge, supervises } from '../console/session.ts';
import { $, act, textInput } from '../console/ui.ts';
import {
  checkboxControl,
  checkedValues,
  selectControl,
  textareaControl,
  textControl,
} from '../controls.ts';

/** «Conocimiento»: a chat with each carrier's assistant, answering only from its documents and the catalogue, and citing them. */

type Citation = {
  documentId: string;
  documentTitle: string;
  passageId: string;
  section: string;
  excerpt: string;
};
type AnswerView = {
  id: string;
  carrier: string;
  productLine: string | null;
  question: string;
  askedBy: string;
  askedAt: string;
  status: 'ANSWERED' | 'NOT_FOUND';
  text: string;
  citations: Citation[];
  conversationId: string;
  requirements: Requirements | null;
};
type RequiredData = { label: string; required: boolean; each?: RequiredData[] };
/** What the catalog says a line needs, when the question asked what it takes to quote or issue. */
type Requirements = {
  productLine: string;
  lineName: string;
  operation: 'QUOTE' | 'ISSUE';
  questionnaire: { id: string; version: number };
  data: RequiredData[];
  documents: string[];
};
type KnowledgeDocView = {
  id: string;
  carrier: string;
  title: string;
  productLines: string[];
  kind: string;
  version: string;
  uploadedBy: string;
  uploadedAt: string;
  archived: { by: string; at: string } | null;
  passages: { id: string; section: string; text: string }[];
};
const DOC_KIND: Record<string, string> = {
  GENERAL_CONDITIONS: 'Condiciones generales',
  UNDERWRITING_MANUAL: 'Manual de suscripción',
  RATE_TABLE: 'Tarifario',
  CIRCULAR: 'Circular',
  PRODUCT_SHEET: 'Ficha de producto',
  OTHER: 'Otro',
};
/** The carrier the knowledge view is on, and the conversation with its assistant (oldest first). */
let kbCarrier = '';
let kbThread: AnswerView[] = [];
/** A new session starts with no carrier chosen and an empty conversation. */
export function resetKnowledge() {
  kbCarrier = '';
  kbThread = [];
}
/** "human:member-test-morales-ana" → "ana"; seeds read as "Nubi (ejemplo)". */
const askedByName = (actor: string) =>
  actor.startsWith('system:')
    ? 'Nubi (ejemplo)'
    : cap(actor.replace(/^human:/, '').replace(/^member-(test-)?[a-z0-9]+-/, ''));
/** Paragraphs of an answer; the numbers in brackets point at the sources below. */
const answerText = (t: string) =>
  t
    .split(/\n{2,}/)
    .map((p) => `<p>${esc(p).replace(/\[(\d+)\]/g, '<sup class="cite-ref">$1</sup>')}</p>`)
    .join('');

const requiredItem = (d: RequiredData): string =>
  `<li>${esc(d.label)}${d.required ? '' : ' <span class="muted">(opcional)</span>'}${
    d.each?.length ? `<ul>${d.each.map(requiredItem).join('')}</ul>` : ''
  }</li>`;

/**
 * A requirements answer, step by step: the catalog's data and documents as checklists, then
 * what the carrier's documents say (the server's text from its third step on).
 */
function requirementsBody(a: AnswerView, r: Requirements) {
  const paragraphs = a.text.split(/\n{2,}/);
  const carrierPart = paragraphs.slice(3);
  const docs =
    r.operation === 'ISSUE'
      ? r.documents.length
        ? `<ul class="kb-checklist">${r.documents.map((d) => `<li>${esc(d)}</li>`).join('')}</ul>`
        : '<p class="muted small">El catálogo no pide documentos para emitir este ramo.</p>'
      : '<p class="muted small">Para cotizar no se piden documentos; se piden al emitir.</p>';
  return `${answerText(paragraphs[0] ?? '')}<ol class="kb-steps">
    <li><h3>Datos del cuestionario</h3><p class="muted small">Catálogo de Nubi, ${esc(r.lineName)}, versión ${r.questionnaire.version}</p><ul class="kb-checklist">${r.data.map(requiredItem).join('')}</ul></li>
    <li><h3>Documentos requeridos para emitir</h3>${docs}</li>
    <li><h3>Lo que dicen los documentos de ${esc(carrierName(a.carrier))}</h3>${answerText(
      carrierPart
        .filter((p) => !/^3\. Lo que dicen los documentos/.test(p))
        .join('\n\n')
        .replace(/^3\. /, ''),
    )}</li></ol>`;
}

function sources(a: AnswerView) {
  if (!a.citations.length) return '';
  return `<h3 class="kb-sources-title">Fuentes</h3><ol class="citations">${a.citations
    .map(
      (c) =>
        `<li class="citation"><div><strong>${esc(c.documentTitle)}</strong> · ${esc(c.section)} <span class="muted small mono">${esc(c.passageId)}</span></div><blockquote>${esc(c.excerpt)}</blockquote></li>`,
    )
    .join('')}</ol>`;
}

/** One question of the conversation and its answer, with the sources it rests on. */
function answerTurn(a: AnswerView, i: number) {
  const state =
    a.status === 'NOT_FOUND'
      ? pill('warn', 'No está en los documentos')
      : a.citations.length
        ? pill('ok', `${a.citations.length} ${a.citations.length === 1 ? 'fuente' : 'fuentes'}`)
        : pill('info', 'Catálogo de Nubi');
  const body = a.requirements ? requirementsBody(a, a.requirements) : answerText(a.text);
  return `<article class="kb-turn" id="kb-turn-${i}" aria-labelledby="kb-q-${i}">
    <div class="kb-turn-head"><p class="kb-question" id="kb-q-${i}">${esc(a.question)}</p>${state}</div>
    <div class="kb-turn-body">${body}${sources(a)}</div></article>`;
}

function conversationCard(thread: AnswerView[]) {
  const last = thread[thread.length - 1];
  if (!last) return '';
  return `<section class="card kb-answer" aria-live="polite" aria-label="Conversación con el asistente"><div class="card-head"><h2>Conversación con ${esc(carrierName(last.carrier))}</h2><span class="spacer"></span>
    <button type="button" class="btn sm" data-kb-new>Nueva conversación</button></div>
    <div class="kb-body">${thread.map(answerTurn).join('')}
    <p class="muted small">Solo con lo que dicen los documentos de ${esc(carrierName(last.carrier))} y el catálogo de Nubi. Confirma con la aseguradora antes de comprometer una cobertura.</p></div></section>`;
}

/** Sections pasted as text: a line "## Título" starts each section. */
function sectionsFrom(raw: string) {
  const out: { heading: string; text: string }[] = [];
  for (const line of raw.split(/\r?\n/)) {
    const h = /^#{1,3}\s+(.+)$/.exec(line.trim());
    if (h?.[1]) out.push({ heading: h[1].trim(), text: '' });
    else if (out.length) {
      const last = out[out.length - 1] as { heading: string; text: string };
      last.text += `${line}\n`;
    } else if (line.trim()) out.push({ heading: 'General', text: `${line}\n` });
  }
  const sections = out.map((s) => ({ heading: s.heading, text: s.text.trim() }));
  const emptyOne = sections.find((s) => !s.text);
  if (!sections.length)
    throw new InputError('Pega el texto del documento, con una línea «## Título» por sección.');
  if (emptyOne) throw new InputError(`La sección «${emptyOne.heading}» no tiene texto.`);
  return sections;
}

export async function conocimiento() {
  if (!canAskKnowledge())
    return (
      pageHead('Conocimiento de aseguradoras', '') +
      empty(
        'lock',
        'Tu rol no consulta el conocimiento de aseguradoras',
        'Pide a dirección, administración o a un agente que lo consulte por ti.',
        '',
        2,
      )
    );
  const carriers = await api<(CarrierView & { productLines: string[] })[]>('GET', '/v1/carriers');
  if (!carriers.some((c) => c.id === kbCarrier)) kbCarrier = carriers[0]?.id ?? '';
  const carrier = carriers.find((c) => c.id === kbCarrier);
  const [docs, recent] = await Promise.all([
    kbCarrier
      ? api<KnowledgeDocView[]>(
          'GET',
          `/v1/knowledge/documents?carrier=${encodeURIComponent(kbCarrier)}`,
        )
      : Promise.resolve([] as KnowledgeDocView[]),
    api<AnswerView[]>('GET', '/v1/knowledge/questions?limit=10'),
  ]);
  const manage = supervises();
  const active = docs.filter((d) => !d.archived);
  const lines = carrier?.productLines ?? [];
  let html = pageHead(
    'Conocimiento de aseguradoras',
    'Conversa con el asistente de cada aseguradora: coberturas, exclusiones, carencias, comisiones o qué necesitas para cotizar o emitir un ramo. Responde solo con sus documentos y el catálogo, y cita de dónde.',
  );
  const thread = kbThread.filter((a) => a.carrier === kbCarrier);
  html += conversationCard(thread);
  html += `<form id="kb-form" class="card" novalidate><div class="card-head"><h2>${thread.length ? 'Sigue la conversación' : 'Pregúntale a la aseguradora'}</h2></div>
    <div class="form-grid">
      <fieldset><legend>Aseguradora</legend>
        ${field(
          'Aseguradora',
          selectControl(
            { id: 'kb-carrier' },
            carriers.map((c) => ({ value: c.id, label: c.name, selected: c.id === kbCarrier })),
          ),
        )}
        ${field('Producto', selectControl({ id: 'kb-line' }, [{ value: '', label: 'Todos' }, ...lines.map((l) => ({ value: l, label: PRODUCT[l] ?? l }))]))}
      </fieldset>
      <fieldset class="span-2"><legend>Tu pregunta</legend>
        ${field('Pregunta', textareaControl({ id: 'kb-question', rows: 3, maxlength: 500, placeholder: thread.length ? '¿Y para accidentes?' : '¿Qué necesito para emitir gastos médicos?' }))}
      </fieldset>
    </div>
    <div class="form-foot"><span class="muted small">${active.length ? `${active.length} ${active.length === 1 ? 'documento vigente' : 'documentos vigentes'} de ${esc(carrier?.name ?? '')}. Si no está en ellos, te lo decimos: no se inventa.` : `${esc(carrier?.name ?? 'Esta aseguradora')} aún no tiene documentos vigentes.`}</span>
      <button class="btn primary" type="submit" id="kb-submit">Preguntar</button></div></form>`;

  const docRows = docs
    .map(
      (
        d,
      ) => `<tr><td><strong>${esc(d.title)}</strong><div class="muted small">${d.passages.length} secciones · versión ${esc(d.version)}</div>
        <details class="kb-sections"><summary>Ver secciones</summary><ol>${d.passages.map((p) => `<li><strong>${esc(p.section)}</strong><p>${esc(p.text)}</p></li>`).join('')}</ol></details></td>
        <td>${esc(DOC_KIND[d.kind] ?? d.kind)}</td>
        <td>${d.productLines.length ? d.productLines.map((l) => esc(PRODUCT[l] ?? l)).join(', ') : 'Todos'}</td>
        <td class="nowrap">${esc(date(d.uploadedAt))}<div class="muted small">${esc(askedByName(d.uploadedBy))}</div></td>
        <td>${d.archived ? pill('CANCELLED', 'Archivado') : pill('ok', 'Vigente')}</td>
        <td class="actions">${manage && !d.archived ? `<button type="button" class="btn sm danger" data-kb-archive="${esc(d.id)}" data-kb-title="${esc(d.title)}">Archivar</button>` : ''}</td></tr>`,
    )
    .join('');
  html += docs.length
    ? `<section class="card"><div class="card-head"><h2>Documentos de ${esc(carrier?.name ?? '')}</h2></div><div class="table-wrap" data-component="table"><table class="table">
      <thead><tr><th>Documento</th><th>Tipo</th><th>Productos</th><th>Agregado</th><th>Estado</th><th class="actions"><span class="sr-only">Acción</span></th></tr></thead>
      <tbody>${docRows}</tbody></table></div>${manage ? '<p class="note">' + icon('alert') + '<span>Un documento archivado deja de usarse en las respuestas; las respuestas anteriores conservan sus citas.</span></p>' : ''}</section>`
    : empty(
        'book',
        `${carrier?.name ?? 'Esta aseguradora'} no tiene documentos`,
        manage
          ? 'Agrega abajo sus condiciones generales, manuales o circulares para que los agentes puedan preguntar.'
          : 'Pide a la dirección que agregue los documentos de esta aseguradora.',
      );

  html += `<section class="card"><div class="card-head"><h2>${manage ? 'Preguntas recientes de la agencia' : 'Tus preguntas recientes'}</h2></div>${
    recent.length
      ? `<div class="table-wrap" data-component="table"><table class="table"><thead><tr><th>Pregunta</th><th>Aseguradora</th>${manage ? '<th>Quién</th>' : ''}<th>Resultado</th><th>Cuándo</th></tr></thead><tbody>${recent
          .map(
            (
              a,
            ) => `<tr><td>${esc(a.question)}</td><td>${esc(carrierName(a.carrier))}</td>${manage ? `<td>${esc(askedByName(a.askedBy))}</td>` : ''}
            <td>${a.status === 'ANSWERED' ? pill('ok', `Con ${a.citations.length} ${a.citations.length === 1 ? 'cita' : 'citas'}`) : pill('warn', 'Sin respuesta')}</td>
            <td class="nowrap">${esc(when(a.askedAt))}</td></tr>`,
          )
          .join('')}</tbody></table></div>`
      : '<p class="muted small kb-empty">Todavía no hay preguntas. Las que hagas quedan registradas aquí.</p>'
  }</section>`;

  if (manage)
    html += `<form id="kb-doc-form" class="card" novalidate><div class="card-head"><h2>Agregar un documento</h2></div>
      <div class="form-grid">
        <fieldset><legend>Documento</legend>
          ${field(
            'Aseguradora',
            selectControl(
              { id: 'kbd-carrier' },
              carriers.map((c) => ({ value: c.id, label: c.name, selected: c.id === kbCarrier })),
            ),
          )}
          ${field('Título', textControl({ id: 'kbd-title', maxlength: 160, placeholder: 'Circular 07-2026: bonos de producción' }))}
          ${field(
            'Tipo',
            selectControl(
              { id: 'kbd-kind' },
              Object.entries(DOC_KIND).map(([k, v]) => ({ value: k, label: v })),
            ),
          )}
          ${field('Versión', textControl({ id: 'kbd-version', maxlength: 40, placeholder: '2026' }))}
          <div class="field"><span>Productos (ninguno = todos)</span><div class="product-checks">${lines
            .map((l) =>
              checkboxControl(
                { class: 'check', 'data-kbd-line': true, value: l },
                esc(PRODUCT[l] ?? l),
              ),
            )
            .join('')}</div></div>
        </fieldset>
        <fieldset class="span-2"><legend>Texto por secciones</legend>
          ${field('Secciones', textareaControl({ id: 'kbd-sections', rows: 10, placeholder: '## Coberturas\nTexto de la sección…\n\n## Exclusiones\nTexto de la sección…' }))}
        </fieldset>
      </div>
      <div class="form-foot"><span class="muted small">Pega el texto y empieza cada sección con una línea «## Título». Cada sección se cita por separado. La carga de PDF llegará después.</span>
        <button class="btn primary" type="submit" id="kbd-submit">Agregar documento</button></div></form>`;
  return html;
}

/** What this view answers: its buttons (by data attribute), forms (by id) and controls (by id). */
export const actions: Actions = {
  click: {
    kbNew: () => {
      kbThread = [];
      void render(current, { quiet: true }).then(() => $('#kb-question').focus());
    },
    kbArchive: (t, d) => {
      if (!d.kbArchive) return;
      void act(t, 'Archivando…', async () => {
        await api(
          'POST',
          `/v1/knowledge/documents/${encodeURIComponent(d.kbArchive ?? '')}/archive`,
          {},
        );
        await render(current, { quiet: true });
        return `«${d.kbTitle ?? ''}» archivado: ya no se usa en las respuestas.`;
      });
    },
  },
  submit: {
    'kb-form': (ev) => {
      ev.preventDefault();
      void act($('#kb-submit'), 'Buscando en los documentos…', async () => {
        const question = textInput('#kb-question', 'la pregunta');
        const line = ($('#kb-line') as HTMLSelectElement).value;
        const before = kbThread.filter((a) => a.carrier === kbCarrier).at(-1);
        const answer = await api<AnswerView>('POST', '/v1/knowledge/questions', {
          carrier: kbCarrier,
          question,
          ...(line ? { productLine: line } : {}),
          ...(before ? { conversationId: before.conversationId } : {}),
        });
        kbThread = [...kbThread.filter((a) => a.carrier === kbCarrier), answer];
        await render(current, { quiet: true });
        document
          .getElementById(`kb-turn-${kbThread.length - 1}`)
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        $('#kb-question').focus();
        return answer.status === 'NOT_FOUND'
          ? `No está en los documentos de ${carrierName(answer.carrier)}.`
          : answer.citations.length
            ? `Respuesta con ${answer.citations.length} ${answer.citations.length === 1 ? 'fuente' : 'fuentes'}.`
            : 'Respuesta con los requisitos del catálogo.';
      });
    },
    'kb-doc-form': (ev) => {
      ev.preventDefault();
      void act($('#kbd-submit'), 'Agregando el documento…', async () => {
        const title = textInput('#kbd-title', 'el título del documento');
        const version = textInput('#kbd-version', 'la versión');
        const sections = sectionsFrom(($('#kbd-sections') as HTMLTextAreaElement).value);
        const carrier = ($('#kbd-carrier') as HTMLSelectElement).value;
        const d = await api<KnowledgeDocView>('POST', '/v1/knowledge/documents', {
          carrier,
          title,
          version,
          kind: ($('#kbd-kind') as HTMLSelectElement).value,
          productLines: checkedValues(document, '[data-kbd-line]'),
          sections,
        });
        kbCarrier = carrier;
        await render(current, { quiet: true });
        return `«${d.title}» agregado con ${d.passages.length} secciones.`;
      });
    },
  },
  change: {
    'kb-carrier': (control) => {
      kbCarrier = control.value;
      kbThread = [];
      void render(current, { quiet: true });
    },
  },
};
